import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { parseNumericInput } from '@jungles/shared';
import { useUser } from '../app/session';
import { DemoPro, readDemoPro } from '../components/DemoPro';
import { CloseIcon } from '../components/icons';
import { ApiError, request } from '../lib/api';
import { foodOrigin } from '../lib/food-origin';
import type { ScanPrefill } from './FoodForm';

type MealResult = { mode: 'meal'; approximate: true; name: string; grams: number; kcal: number; protein: number; fat: number; carbs: number; notes: string | null };
type Photo = { url: string; base64: string; mediaType: 'image/jpeg' | 'image/png' | 'image/webp' };
export function MealPhotoScreen() {
  const user = useUser();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const origin = foodOrigin(useLocation().state);
  const [enabled, setEnabled] = useState(() => readDemoPro(user.id));
  const [photo, setPhoto] = useState<Photo | null>(null);
  const [description, setDescription] = useState('');
  const [grams, setGrams] = useState('');
  const [error, setError] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [busy, setBusy] = useState(false);
  const [facing, setFacing] = useState<'environment' | 'user'>('environment');
  const video = useRef<HTMLVideoElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const generation = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const stop = () => { stream.current?.getTracks().forEach(track => track.stop()); stream.current = null; };
  const invalidate = () => { generation.current++; controller.current?.abort(); controller.current = null; };
  useEffect(() => () => { invalidate(); stop(); }, []);
  useEffect(() => {
    if (!enabled || photo) return;
    let cancelled = false;
    setCameraError('');
    void (async () => {
      try {
        const next = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: facing }, width: { ideal: 1600 } }, audio: false });
        if (cancelled) { next.getTracks().forEach(t => t.stop()); return; }
        stream.current = next;
        if (video.current) { video.current.srcObject = next; await video.current.play().catch(() => undefined); }
      } catch { if (!cancelled) setCameraError('Камера недоступна. Выберите фото из галереи или добавьте блюдо вручную.'); }
    })();
    return () => { cancelled = true; stop(); };
  }, [enabled, photo, facing]);
  const query = new URLSearchParams({ meal: params.get('meal') ?? 'snack', date: params.get('date') ?? '' }).toString();
  const close = () => {
    invalidate(); stop();
    navigate(origin?.path ?? `/nutrition?date=${params.get('date') ?? ''}`, { replace: true, state: origin ? { restoreFoodOrigin: origin } : undefined });
  };
  useEffect(() => { const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); }; window.addEventListener('keydown', escape); return () => window.removeEventListener('keydown', escape); });
  const capture = () => {
    const source = video.current;
    if (!source || source.readyState < 2) { setError('Камера ещё не готова'); return; }
    const canvas = document.createElement('canvas');
    const scale = Math.min(1, 1600 / Math.max(source.videoWidth, source.videoHeight));
    canvas.width = Math.round(source.videoWidth * scale); canvas.height = Math.round(source.videoHeight * scale);
    const context = canvas.getContext('2d');
    if (!context) { setError('Не удалось обработать снимок'); return; }
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    const url = canvas.toDataURL('image/jpeg', .85);
    invalidate(); stop(); setError(''); setPhoto({ url, base64: url.split(',')[1] ?? '', mediaType: 'image/jpeg' });
  };
  const selectFile = async (selected?: File) => {
    if (!selected) return;
    invalidate(); const current = generation.current; setError('');
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(selected.type) || selected.size > 5 * 1024 * 1024) {
      setError('Выберите JPEG, PNG или WebP размером до 5 МБ.'); return;
    }
    try {
      const url = await new Promise<string>((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = reject; reader.readAsDataURL(selected); });
      if (current !== generation.current) return;
      stop(); setPhoto({ url, base64: url.split(',')[1] ?? '', mediaType: selected.type as Photo['mediaType'] });
    } catch { if (current === generation.current) setError('Не удалось открыть фото. Выберите другое.'); }
  };
  const recognize = async () => {
    if (!photo || controller.current) return;
    const amount = grams.trim() ? parseNumericInput(grams) : undefined;
    if (amount !== undefined && (!Number.isFinite(amount) || amount < 1 || amount > 5000)) { setError('Вес порции — от 1 до 5000 г'); return; }
    invalidate(); const current = generation.current; const abort = new AbortController(); controller.current = abort;
    setBusy(true); setError('');
    try {
      const result = await request<MealResult>('/api/foods/photo', { method: 'POST', signal: abort.signal, body: { mode: 'meal', imageBase64: photo.base64, mediaType: photo.mediaType, ...(description.trim() ? { description: description.trim() } : {}), ...(amount !== undefined ? { grams: amount } : {}) } });
      if (abort.signal.aborted || current !== generation.current) return;
      const prefill: ScanPrefill = { name: result.name, brand: null, grams: result.grams, basis: 'perServing', kcal: result.kcal, protein: result.protein, fat: result.fat, carbs: result.carbs, source: 'photo', nutritionBasis: 'per100g', notes: result.notes };
      stop(); navigate(`/nutrition/food?${query}`, { replace: true, state: { foodOrigin: origin, prefill } });
    } catch (err) {
      if (abort.signal.aborted || current !== generation.current) return;
      const reason = err instanceof ApiError ? (err.payload as { reason?: string } | null)?.reason : null;
      setError(reason === 'not_configured' ? 'Распознавание фото пока не настроено. Демо включено, но сервис недоступен. Добавьте блюдо вручную или попробуйте позже.' : err instanceof ApiError ? err.message : 'Не удалось распознать фото. Попробуйте ещё раз или добавьте вручную.');
    } finally { if (current === generation.current) { controller.current = null; setBusy(false); } }
  };
  return <div className="meal-photo">
    <div className="photo-top"><button className="round" aria-label="Закрыть" onClick={close}><CloseIcon /></button><b>Блюдо по фото</b>{photo ? <button className="b" onClick={() => { invalidate(); setBusy(false); setPhoto(null); setError(''); }}>Переснять</button> : <span>Демо Pro</span>}</div>
    <div className={`photo-preview ${photo ? '' : 'live'}`}>
      {photo ? <img src={photo.url} alt="Выбранное фото блюда" /> : <video ref={video} autoPlay playsInline muted />}
    </div>
    {photo ? <section className="photo-details"><h2>Что на фото?</h2><p className="nt">Подсказки необязательны, но сделают расчёт точнее</p>
      <label className="fl">Название или описание<input className="f" maxLength={300} placeholder="Например: паста карбонара, без сливок" value={description} onChange={e => setDescription(e.target.value)} disabled={busy} /></label>
      <label className="fl">Вес порции, г<input className="f" inputMode="decimal" placeholder="Например, 350" value={grams} onChange={e => setGrams(e.target.value)} disabled={busy} /></label>
      <p className="nt">Без веса оценим порцию по фото. Результат приблизительный.</p>
      <button className="b p wide" disabled={busy} onClick={() => void recognize()}>{busy ? 'Считаем КБЖУ…' : 'Посчитать КБЖУ'}</button>
    </section> : <><p className="photo-instruction">Сфотографируйте блюдо сверху<small>Вся тарелка в кадре, при хорошем свете</small></p>{cameraError && <p className="photo-error">{cameraError}</p>}<div className="photo-controls">
      <button className="b" onClick={() => file.current?.click()}>Галерея</button><button className="photo-shutter" aria-label="Сфотографировать блюдо" disabled={!enabled || !!cameraError} onClick={capture} /><button className="b" aria-label="Повернуть камеру" onClick={() => setFacing(facing === 'user' ? 'environment' : 'user')}>Повернуть</button>
    </div></>}
    <input ref={file} type="file" accept="image/jpeg,image/png,image/webp" aria-label="Фото из галереи" hidden onChange={e => { void selectFile(e.target.files?.[0]); e.target.value = ''; }} />
    {error && <p className="photo-error" role="alert">{error}</p>}
    <button className="b ghost wide photo-cancel" onClick={() => { invalidate(); stop(); navigate(`/nutrition/food?${query}`, { replace: true, state: { foodOrigin: origin } }); }}>Добавить вручную</button>
    {!enabled && <DemoPro onClose={close} onEnabled={() => setEnabled(true)} />}
  </div>;
}
